import { Grid, CardHeader, CardContent, Box, Select, FormHelperText, MenuItem, TextField, CircularProgress, Avatar, Button, InputLabel, FormControl } from "@mui/material";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Form, FormikProvider, useFormik, FieldArray } from "formik";
import { IMAGE_BASE } from "../../utils/constants";
import LoadingButton from "@mui/lab/LoadingButton";
import CardLayout from "../../layouts/CardLayout";
import { toast } from "react-toastify";
import errorHandler from "../../utils/errorHandler";
import { FreeBidManagementSchema } from "../../utils/Schema";
import { apiAdsBidFree } from "../../services/AllServices";
import { Upload } from "@mui/icons-material";

function AddEditBudFreeManagement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [videoLoading, setVideoLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [data, setData] = useState();
  const { id } = useParams();

  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await apiAdsBidFree.getById({ id });
        const resData = response?.data?.data;
        setImagePreview(IMAGE_BASE + resData?.thumbnailImage);
        setSelectedVideo(IMAGE_BASE + resData?.video)
        setVideoPreview(IMAGE_BASE + resData?.video);
        setData(resData);
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: FreeBidManagementSchema,
    initialValues: {
      type: data?.type || "",
      freeBid: data?.freeBid || "",
      videoLength: data?.videoLength || "",
      description: data?.description || "",
      thumbnailImage: data?.thumbnailImage || "",
      video: data?.video || "",
      adsID: data?.adsID || "",
      status: data?.status || "active",
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const formData = new FormData();

        if (id) {
          formData.append("id", id);
        }
        formData.append("type", values.type);
        formData.append("freeBid", values.freeBid);
        formData.append("videoLength", values.videoLength);
        formData.append("description", values.description);
        formData.append("adsID", values.adsID);
        formData.append("status", values.status);
        formData.append("filePath", "video");

        if (selectedImage && selectedImage !== data?.thumbnailImage) {
          formData.append("thumbnailImage", selectedImage);
        }
        if (selectedVideo && selectedVideo !== data?.video) {
          formData.append("video", selectedVideo);
        } else if (data?.video && !selectedVideo) {
          formData.append("video", data.video);
        }

        const response = id
          ? await apiAdsBidFree.edit(formData)
          : await apiAdsBidFree.add(formData);

        toast.success(response?.data?.message, { toastId: "success" });
        navigate(-1);
      } catch (error) {
        console.error(error);
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleImageChange = (event) => {
    const file = event.currentTarget.files[0];
    const validTypes = ["image/jpeg", "image/png", "image/gif"];

    if (selectedImage && selectedImage.name === file?.name) {
      toast.error("You cannot upload the same humbnail image again.");
      imageInputRef.current.value = "";
      return;
    }

    if (file && validTypes.includes(file.type)) {
      setImageLoading(true);
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
      formik.setFieldValue("thumbnailImage", file);
      setTimeout(() => {
        setImageLoading(false);
      }, 2000);
    } else {
      toast.error("Please upload a valid image (JPEG, PNG, or GIF).");
      imageInputRef.current.value = "";
    }
  };

  // const handleVideoChange = (event) => {
  //   const file = event.currentTarget.files[0];
  //   const validTypes = ["video/mp4", "video/ogg", "video/webm", "video/quicktime", "video/x-msvideo"];



  //   if (selectedVideo && selectedVideo.name === file?.name) {
  //     toast.error("You cannot upload the same video again.");
  //     videoInputRef.current.value = "";
  //     return;
  //   }


  //   if (file && validTypes.includes(file.type)) {
  //     setVideoLoading(true);
  //     setSelectedVideo(file);
  //     setVideoPreview(URL.createObjectURL(file));
  //     formik.setFieldValue("video", file);
  //     setTimeout(() => {
  //       setVideoLoading(false);
  //     }, 2000);
  //   } else {
  //     toast.error("Please upload a valid video (MP4, OGG, or WEBM).");
  //     videoInputRef.current.value = "";
  //   }
  // };

  const handleVideoChange = (event) => {
    const file = event.currentTarget.files[0];
    if (!file) return;

    const validTypes = ["video/mp4", "video/ogg", "video/webm", "video/quicktime", "video/x-msvideo"];
    const validExtensions = ["mp4", "ogg", "webm", "mov", "avi"];
    const fileExtension = file.name.split('.').pop().toLowerCase();

    if (selectedVideo && selectedVideo.name === file.name) {
      toast.error("You cannot upload the same video again.");
      videoInputRef.current.value = "";
      return;
    }

    if (validTypes.includes(file.type) || validExtensions.includes(fileExtension)) {
      setVideoLoading(true);

      // Create a temporary URL for validation
      const videoURL = URL.createObjectURL(file);
      const videoElement = document.createElement("video");
      videoElement.preload = "metadata";
      videoElement.src = videoURL;

      videoElement.onloadeddata = () => {
        setSelectedVideo(file);
        setVideoPreview(videoURL);
        formik.setFieldValue("video", file);
        setVideoLoading(false);
      };

      videoElement.onerror = () => {
        console.error("Corrupt or unsupported video file.");
        toast.error("Corrupt or unsupported video file. Please try again.");
        videoInputRef.current.value = "";
        setVideoLoading(false);
      };
    } else {
      toast.error("Invalid video format. Please upload MP4, OGG, WEBM, MOV, or AVI.");
      videoInputRef.current.value = "";
    }
  };

  const handleFreeBidChange = (event) => {
    const value = event.target.value;
    const validNumberRegex = /^\d{0,5}(\.\d{0,2})?$/;
    if (value === "" || (validNumberRegex.test(value) && value.length <= 8)) {
      formik.setFieldValue("freeBid", value);
    }
  };


  const handleVideoLengthChange = (event) => {
    const value = event.target.value;
    const validNumberRegex = /^[0-9]{0,5}$/;

    if (value === "" || (validNumberRegex.test(value) && value.length <= 5)) {
      formik.setFieldValue("videoLength", value);
    }
  };


  return (
    <Box>
      <title>{id ? "Edit" : "Add"} Watch Ads and Get Free Bid Management</title>
      <CardLayout>
        <CardHeader title={`${id ? "Edit" : "Add"} Watch Ads and Get Free Bid Management`} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>

                <Grid item xs={12} md={12}>
                  <input
                    accept="image/*"
                    type="file"
                    ref={imageInputRef}
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                  />

                  <Box
                    mt={2}
                    onClick={() => imageInputRef.current.click()}
                    className="upload-box"
                  >
                    {imageLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <Avatar
                        src={imagePreview || "/path/to/default-avatar.jpg"}
                        alt="preview"
                        className="avatar"
                      />
                    )}
                  </Box>
                  {formik.touched.thumbnailImage && formik.errors.thumbnailImage ? (
                    <div className="error-msg error-category">
                      {formik.errors.thumbnailImage}
                    </div>
                  ) : null}

                  <Box mt={2} display="flex" justifyContent="center">
                    <Button
                      size="large"
                      type="button"
                      variant="contained"
                      onClick={() => imageInputRef.current.click()}
                    >
                      {id ? "Edit" : "Upload"} Thumbnail Image
                    </Button>
                  </Box>
                </Grid>

                <Grid item xs={6} md={6}>
                  <FormControl fullWidth required error={Boolean(formik.touched.type && formik.errors.type)}>

                    <InputLabel id="type-label">Types of Ads</InputLabel>
                    <Select
                      labelId="type-label"
                      id="type"
                      required
                      value={formik.values.type}
                      onChange={(e) => formik.setFieldValue("type", e.target.value)}
                      fullWidth
                      error={Boolean(formik.touched.type && formik.errors.type)}
                      label="Types of Ads"
                    >
                      <MenuItem value={"daily"}>Daily</MenuItem>
                      <MenuItem value={"weekly"}>Weekly</MenuItem>
                    </Select>
                    {formik.touched.type && formik.errors.type && (
                      <FormHelperText error>{formik.errors.type}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>


                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    required
                    type="text"
                    label={"Free Bids"}
                    value={formik.values.freeBid}
                    onChange={handleFreeBidChange}
                    name="freeBid"
                    error={Boolean(formik.touched.freeBid && formik.errors.freeBid)}
                    helperText={formik.touched.freeBid && formik.errors.freeBid}
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>


                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    required
                    type="text"
                    label="Video Length"
                    value={formik.values.videoLength}
                    onChange={handleVideoLengthChange}
                    name="videoLength"
                    error={Boolean(formik.touched.videoLength && formik.errors.videoLength)}
                    helperText={formik.touched.videoLength && formik.errors.videoLength}
                    inputProps={{ maxLength: 5 }}
                  />
                </Grid>

                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    required
                    name="description"
                    label="Description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    error={Boolean(formik.touched.description && formik.errors.description)}
                    helperText={formik.touched.description && formik.errors.description}
                    inputProps={{ maxLength: 50 }}
                  />
                </Grid>

                <Grid item xs={6} md={6}>
                  <TextField
                    fullWidth
                    required
                    name="adsID"
                    label="Ads ID"
                    value={formik.values.adsID}
                    onChange={(e) => {
                      const value = e.target.value;
                      const validNumberRegex = /^[0-9]*$/;
                      if (value === "" || (validNumberRegex.test(value) && value.length <= 15)) {
                        formik.setFieldValue("adsID", value);
                      }
                    }}
                    error={Boolean(formik.touched.adsID && formik.errors.adsID)}
                    helperText={formik.touched.adsID && formik.errors.adsID}
                    inputProps={{ maxLength: 15 }}
                  />
                </Grid>


                {/* <Grid item xs={12} md={12}>
                  <input
                    accept="video/*"
                    type="file"
                    ref={videoInputRef}
                    onChange={handleVideoChange}
                    style={{ display: "none" }}
                  />

                  <Box mt={2} display="flex">
                    <Button
                      size="large"
                      type="button"
                      variant="contained"
                      onClick={() => videoInputRef.current.click()}
                    >
                      {id ? "Edit" : "Upload"} Video
                      <Upload />
                    </Button>
                  </Box>
                  <Box mt={2} display="flex">
                    {formik.touched.video && formik.errors.video ? (
                      <div className="error-msg error-category">
                        {formik.errors.video}
                      </div>
                    ) : null}
                  </Box>
                  {selectedVideo ?
                    <Box
                      mt={12}
                    >
                      {videoLoading ? (
                        <CircularProgress size={24} />
                      ) : (
                        <video
                          src={videoPreview || "/path/to/default-video.mp4"}
                          controls
                          style={{ width: "100%" }}
                        />
                      )}
                    </Box>
                    : ''}
                </Grid> */}

                {id ?
                  <Grid item xs={12} md={12}>
                    <Select
                      labelId="status-label"
                      id="status"
                      value={formik.values.status}
                      onChange={(e) =>
                        formik.setFieldValue("status", e.target.value)
                      }
                      fullWidth
                      error={Boolean(
                        formik.touched.status && formik.errors.status
                      )}
                      displayEmpty
                      defaultValue=""
                    >
                      <MenuItem value="">Select Status</MenuItem>
                      <MenuItem value={"active"}>Active</MenuItem>
                      <MenuItem value={"inactive"}>Inactive</MenuItem>
                    </Select>
                    {formik.touched.status && formik.errors.status && (
                      <FormHelperText error>
                        {formik.errors.status}
                      </FormHelperText>
                    )}
                  </Grid>
                  : ""}
                <Grid item xs={12}>
                  <Box mt={3} width="100%">
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                    >
                      {id ? "Update" : "Add"} Ad
                    </LoadingButton>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
}

export default AddEditBudFreeManagement;
